import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { VisuallyHiddenInput } from './Checkbox';

const ToggleContainer = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  user-select: none;
`;

interface ToggleTrackProps {
  checked: boolean;
  disabled?: boolean;
}

const ToggleTrack = styled('span', {
  shouldForwardProp: prop => isPropValid(prop) && !['checked', 'disabled'].includes(prop)
})<ToggleTrackProps>`
  position: relative;
  width: 36px;
  height: 20px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: ${({ checked, theme }) =>
    checked ? theme.accent.primary : theme.background.tertiary};
  border: 1px solid ${({ checked, theme }) =>
    checked ? theme.accent.primary : theme.border.color.medium};
  transition: ${({ theme }) => theme.animation.transition.standard};
  flex-shrink: 0;

  ${({ disabled }) => disabled && `opacity: 0.5; cursor: not-allowed;`}

  ${VisuallyHiddenInput}:focus-visible + & {
    outline: none;
    box-shadow: ${({ theme }) => theme.focusShadow(theme.accent.tertiary)};
  }
`;

interface ToggleThumbProps {
  checked: boolean;
}

const ToggleThumb = styled('span', {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'checked'
})<ToggleThumbProps>`
  position: absolute;
  top: 2px;
  left: ${({ checked }) => checked ? '18px' : '2px'};
  width: 14px;
  height: 14px;
  border-radius: ${({ theme }) => theme.border.radius.rounded};
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: left 0.2s ease;
`;

const ToggleLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.primary};
`;

interface ToggleProps {
  checked: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  label?: string;
  id?: string;
}

export function Toggle({
  checked,
  onChange,
  disabled,
  label,
  id,
  ...props
}: ToggleProps) {
  return (
    <ToggleContainer>
      <VisuallyHiddenInput
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        id={id}
        aria-checked={checked}
        {...props}
      />
      <ToggleTrack checked={checked} disabled={disabled}>
        <ToggleThumb checked={checked} />
      </ToggleTrack>
      {label && <ToggleLabel>{label}</ToggleLabel>}
    </ToggleContainer>
  );
}
